import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import styled, { css } from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";

import { VisuallyHidden } from "./visuallyHidden";

const { color, typeScale } = adminTokens;
// The design's secondary identifier line is 12px, one step under the meta style.
const secondaryFontSize = "12px";
const linkFontSize = "13px";

const Identifier = styled.div`
  display: flex;
  flex-direction: column;
`;

const Primary = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${typeScale.labelValue.fontSize};
  line-height: ${typeScale.labelValue.lineHeight};
`;

const Secondary = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${secondaryFontSize};
`;

interface IdentifierCellProps {
  primary: ReactNode;
  secondary?: ReactNode;
}

// An identifier with the one that goes with it below (TABLE-R7).
export const IdentifierCell = ({ primary, secondary }: IdentifierCellProps) => (
  <Identifier>
    <Primary>{primary}</Primary>
    {secondary && <Secondary>{secondary}</Secondary>}
  </Identifier>
);

const linkStyle = css`
  color: ${color.tealDark};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${linkFontSize};
  overflow-wrap: anywhere;

  &:focus-visible {
    ${focusOutline}
  }
`;

const Link = styled.a`
  ${linkStyle}
`;

const ScreenLink = styled(RouterLink)`
  ${linkStyle}
`;

const LinkLookButton = styled.button`
  ${linkStyle}
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;

interface ScreenLinkButtonProps {
  // The name a screen reader reads, fuller than the visible text (CASES-R20, ORDERS-R9).
  name: string;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}

// A Button in the link style of TABLE-R29, for an action that opens another admin view in place.
export const ScreenLinkButton = ({ name, title, onClick, children }: ScreenLinkButtonProps) => (
  <LinkLookButton type="button" aria-label={name} title={title} onClick={onClick}>
    {children}
  </LinkLookButton>
);

interface ExternalLinkProps {
  href: string;
  // Hover text, such as the whole identifier when the link shows a short form (TABLE-R28).
  title?: string;
  children: ReactNode;
}

// A link to another system: the arrow says so, and the new tab gets no access
// to the admin (TABLE-R8). A screen reader reads the text as its name and the new
// tab as its description (TABLE-R26).
export const ExternalLink = ({ href, title, children }: ExternalLinkProps) => {
  const descriptionId = `${useFieldId()}-new-tab`;

  return (
    <>
      <Link
        href={href}
        title={title}
        target="_blank"
        rel="noopener noreferrer"
        aria-describedby={descriptionId}
      >
        {children}
        <span aria-hidden="true">&nbsp;↗</span>
      </Link>
      <VisuallyHidden id={descriptionId}>Opens in a new tab</VisuallyHidden>
    </>
  );
};

interface AdminLinkProps {
  to: string;
  children: ReactNode;
}

// A link to another admin screen: same tab, so no arrow (TABLE-R29).
export const AdminLink = ({ to, children }: AdminLinkProps) => (
  <ScreenLink to={to}>{children}</ScreenLink>
);

// The characters before an identifier's first hyphen; an identifier with no hyphen is whole
// (TABLE-R28).
export const shortenIdentifier = (identifier: string): string => identifier.split("-")[0];

// A long identifier shown by its first segment, the whole identifier on hover (TABLE-R28).
export const ShortIdentifier = ({ value }: { value: string }) => (
  <span title={value}>{shortenIdentifier(value)}</span>
);
